import React from 'react';
import { Card, Collapse, Tag, Button, Typography, Tooltip } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import type { CockpitGroupResult, CockpitTableResult } from '../../../../services/devApi';
import { parseStatusNote, translateStatusValue, cellText } from './helpers';
import { getFormRoute } from './formRoutes';
import RowsTable from './RowsTable';

const { Text } = Typography;

interface Props {
  group: CockpitGroupResult;
}

/** 單張 table 的結果徽章 + 狀態 Tag（Collapse header） */
const TableHeader: React.FC<{ table: CockpitTableResult }> = ({ table }) => {
  const noteMap = parseStatusNote(table.note);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Tag className="font-mono" style={{ marginRight: 0 }}>
        {table.table}
      </Tag>
      <span>{table.label}</span>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {table.role}
      </Text>
      {table.count > 0 ? (
        <Tag color="green" style={{ marginRight: 0 }}>
          ✅ {table.count} 筆
        </Tag>
      ) : (
        <Tag style={{ marginRight: 0, color: '#999' }}>— 0 筆</Tag>
      )}
      {table.statusValues &&
        Object.entries(table.statusValues).map(([col, val]) => (
          <Tooltip key={col} title={table.note || undefined}>
            <Tag color="blue" className="font-mono" style={{ marginRight: 0 }}>
              {col} = {translateStatusValue(cellText(val), noteMap)}
            </Tag>
          </Tooltip>
        ))}
    </div>
  );
};

/** 流程足跡：一個 group（表單）一張 Card，內含各 table 的可展開結果列 */
const TraceGroupCard: React.FC<Props> = ({ group }) => {
  const handleOpenForm = () => {
    window.open(getFormRoute(group.formId), '_blank');
  };

  return (
    <Card
      size="small"
      title={`📋 ${group.label}`}
      extra={
        <Button size="small" icon={<ExportOutlined />} onClick={handleOpenForm}>
          開啟表單 ↗
        </Button>
      }
    >
      {group.tables.length === 0 ? (
        <Text type="secondary">此表單沒有追蹤的資料表</Text>
      ) : (
        <Collapse
          ghost
          size="small"
          items={group.tables.map((t) => ({
            key: t.table,
            label: <TableHeader table={t} />,
            collapsible: t.rows && t.rows.length > 0 ? undefined : ('disabled' as const),
            children: <RowsTable rows={t.rows || []} />,
          }))}
        />
      )}
    </Card>
  );
};

export default TraceGroupCard;
